import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { cn } from "../utils/cn.js";
import { DateTime } from "luxon";
import { useIsGummy } from "../hooks/useGummyTheme.js";

type NoteData = {
  id: number | string;
  name?: string | null;
  description?: string | null;
  content: string;
  created_at: string;
  updated_at?: string;
};

type NoteCardProps = {
  note: NoteData;
  className?: string;
  onClick?: () => void;
};

const extractPreview = (html: string, maxLength = 120): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const text = doc.body.textContent || "";
  const trimmed = text.trim();
  if (trimmed.length <= maxLength) return trimmed;
  return trimmed.slice(0, maxLength).trim() + "...";
};

const extractTitle = (note: NoteData): string => {
  if (note.name) return note.name;
  if (note.description) return note.description;

  const h1Match = note.content.match(/<h[1-6][^>]*>(.*?)<\/h[1-6]>/i);
  if (h1Match) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(h1Match[1], "text/html");
    return doc.body.textContent?.trim() || "Untitled";
  }

  return "Untitled";
};

const NoteCard = ({ note, className, onClick }: NoteCardProps) => {
  const isGummy = useIsGummy();
  const title = extractTitle(note);
  const preview = extractPreview(note.content).replace(title, "");
  const date = DateTime.fromISO(note.created_at);

  const Component = onClick ? "button" : "div";

  if (isGummy) {
    return (
      <Component
        onClick={onClick}
        className={cn(
          "shadow-card",
          "group relative flex flex-col rounded-surface-lg bg-white dark:bg-mono-900 p-6 text-left",
          "transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.98]",
          "motion-reduce:transform-none motion-reduce:transition-none",
          "outline-none focus-visible:ring-4 focus-visible:ring-primary-400/40",
          onClick && "cursor-pointer",
          className
        )}
      >
        <div className="flex items-start gap-4">
          <div
            className={cn(
              "flex h-14 w-14 shrink-0 items-center justify-center rounded-surface-sm text-white",
              "bg-tile-schedule",
              "shadow-raised text-shadow-on-color-strong"
            )}
          >
            <DocumentTextIcon className="size-7" strokeWidth={2.5} />
          </div>
          <div className="min-w-0 flex-1">
            <h5 className="truncate text-xl font-black tracking-tight text-mono-900 dark:text-white">
              {title}
            </h5>
            <p className="mt-1.5 text-xs font-black uppercase tracking-widest text-mono-600 dark:text-mono-300">
              {date.toFormat("MMM dd, yyyy")}
            </p>
          </div>
        </div>

        {preview && (
          <p className="mt-4 line-clamp-2 text-base font-bold leading-snug text-mono-700 dark:text-mono-200">
            {preview}
          </p>
        )}
      </Component>
    );
  }

  return (
    <Component
      onClick={onClick}
      className={cn(
        "group relative flex flex-col rounded-xl border border-mono-200 bg-white p-4 text-left transition-all hover:border-mono-300 hover:shadow-md dark:border-mono-700/50 dark:bg-mono-800/50 dark:hover:border-mono-600 dark:hover:bg-mono-800",
        onClick && "cursor-pointer",
        className
      )}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-500 dark:bg-amber-950 dark:text-amber-400">
          <DocumentTextIcon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h5 className="truncate text-sm font-medium text-mono-900 dark:text-mono-100">
            {title}
          </h5>
          <p className="mt-0.5 text-xs text-mono-500 dark:text-mono-400">
            {date.toFormat("MMM dd, yyyy")}
          </p>
        </div>
      </div>

      {preview && (
        <p className="mt-3 line-clamp-2 text-sm text-mono-600 dark:text-mono-300">
          {preview}
        </p>
      )}
    </Component>
  );
};

const NoteCardSkeleton = () => (
  <div className="animate-pulse flex flex-col rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-700/50 dark:bg-mono-800/50">
    <div className="flex items-start gap-3">
      <div className="h-9 w-9 shrink-0 rounded-lg bg-mono-200 dark:bg-mono-700" />
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="h-4 w-3/4 rounded bg-mono-200 dark:bg-mono-700" />
        <div className="h-3 w-1/3 rounded bg-mono-200 dark:bg-mono-700" />
      </div>
    </div>
    <div className="mt-3 space-y-1.5">
      <div className="h-3 w-full rounded bg-mono-200 dark:bg-mono-700" />
      <div className="h-3 w-2/3 rounded bg-mono-200 dark:bg-mono-700" />
    </div>
  </div>
);

export { NoteCard, NoteCardSkeleton };
export type { NoteData, NoteCardProps };
